import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { JSX } from 'react/jsx-runtime';
import cx from 'classnames';
import { useNavigate, Link } from 'react-router-dom';
import { User, getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc, collection, updateDoc } from "firebase/firestore";
import { Alert, Typography } from "@mui/material";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import * as service from '../../../services/api';
import { ENV } from '../../../config/env';
// utils
import {
  getBillingCycleText,
  getMembershipSource,
  getMonthsText,
  getYearsText,
  getPlanPrice,
  getRegistrationType
} from '../../../utils/registration';
// Types
import { AccountStateType, AccountActionType, SubscriptionType } from '../../../types/account';
import { PlanType } from '../../../types/register';
// Components
import { capitaliseFirstLetter, convertUnitAmountToAmount } from '../../../components/Common/helperFunction';
import { TrialStatus } from './AccountTrialStatus';
import { ExpiredStatus } from './AccountExpiredStatus';
import { ActiveStatus } from './AccountActiveStatus';
import { SuspendedStatus } from './AccountSuspendedStatus';
// Hooks
import { useFirebase } from '../../../hooks/useFirebase';
// Styles
import './Account.styles.scss';

const calculateDurationInMonths = (interval_count: any, interval: any) => {
  return interval_count
}

const getInterval = (duration: any, interval: any) => {
  if (duration > 1) {
    switch (interval) {
      case 'DAY':
        return 'DAYS';
      case 'MONTH':
        return 'MONTHS';
      case 'YEAR':
        return 'YEARS';
      default:
        return interval;
    }
  } else {
    return interval
  }
}

const getPaypalProductDetail = (items: any, billingCycle: any): PlanType | undefined => {
  if (!items?.id || !billingCycle?.length) {
    return undefined;
  }
  const plan: PlanType = {
    id: items.id,
    name: `${capitaliseFirstLetter(items.name)}`,
    durationMonths: calculateDurationInMonths(billingCycle[0].frequency?.interval_count, billingCycle[0].frequency?.interval_unit),
    price: billingCycle[0].pricing_scheme?.fixed_price?.value,
    billingCycle: billingCycle[0].frequency?.interval_unit === "YEAR" ? "annually" : billingCycle[0].frequency?.interval_unit,
    billing_cycles: billingCycle[0].frequency?.interval_unit === "YEAR" ? "annually" : billingCycle[0].frequency?.interval_unit,
    product: items.product_id,
    trialPeriod: billingCycle[1]?.frequency?.interval_count,
    interval: getInterval(billingCycle[0].frequency?.interval_count, billingCycle[0].frequency?.interval_unit),
    recurring: {
      interval: billingCycle[0].frequency?.interval_unit,
      interval_count: billingCycle[0].frequency?.interval_count,
    },
    unit_amount: billingCycle[0].pricing_scheme?.fixed_price?.value,
  };
  return plan;
};

const getExpiredSubscription = (): SubscriptionType => ({
  cancelled: false,
  endDate: 0,
  endedAt: null,
  onTrial: false,
  startDate: 0,
  status: 'expired',
});

const fetchSubscriptionData = async (transactionReceipt: string | undefined) => {
  if (!transactionReceipt) return null;
  try {
    const response = await fetch(service.API_ENDPOINTS.receiptValidation, {
      method: 'POST',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify({ sub: transactionReceipt }),
    });
    const responseBody = await response.json().catch(() => null);

    if (response.status === 404 && responseBody?.code === 'resource_missing') {
      return getExpiredSubscription();
    }

    if (!response.ok || !responseBody || responseBody.status === false) {
      console.error('Receipt validation failed:', responseBody?.message ?? response.statusText);
      return null;
    }

    return responseBody as SubscriptionType;
  } catch (error) {
    console.error('Receipt validation request failed:', error);
    return null;
  }
};

function resolvePlanFromUser(
  userData: any,
  products: any[] | undefined,
  subscription: SubscriptionType | null | undefined
): PlanType | undefined {

  if (!userData || !products) return undefined;
  const membershipSource = userData?.subscriptionPlan === 'Free access' ? 'Free Access' : getMembershipSource(userData?.transactionReceipt);
  const isMemberStripe = membershipSource === 'Stripe';
  const isMemberPaypal = membershipSource === 'PayPal';
  if (isMemberStripe) {
    return products?.find((product: any) => product.id === userData?.subscriptionPlan);
  }
  if (isMemberPaypal) {
    return getPaypalProductDetail(subscription?.items, subscription?.items?.billing_cycles);
  }
  return capitaliseFirstLetter(userData?.subscriptionPlan?.replace(/_/g, ' '));
}

type AccountPageProps = {
  state: AccountStateType,
  dispatch: (action: AccountActionType) => void
}

export const Account: React.FC<AccountPageProps> = ({ state, dispatch }) => {
  const navigate = useNavigate();
  const firebaseApp = useFirebase();
  const auth = firebaseApp ? getAuth(firebaseApp) : null;
  const { doc: firebaseDocumentData, loading: isLoading, loadingText, subscription, tab: currentTab, user: currentUser, products } = state;
  const isPaymentMethodOnFile = !!subscription?.paymentMethod?.last4;
  const [isError, setisError] = useState(true);

  const updateOldPrices = useCallback(async (userData: any, userId: string) => {
    const oldPrices = ['price_1IzH9jAb6wabgBHHMfKRtXVU', 'price_1IzH9eAb6wabgBHHFWdLj0ak', 'price_1Ihq1MAb6wabgBHHTlzxRGfx', 'price_1Ihq1gAb6wabgBHHRydB0wvo'];
    let updatedUserData = { ...userData };

    if (userData?.subscriptionPlan === oldPrices[0] || userData?.subscriptionPlan === oldPrices[2]) {
      updatedUserData = { ...userData, subscriptionPlan: products && products[0] };
    } else if (userData?.subscriptionPlan === oldPrices[1] || userData?.subscriptionPlan === oldPrices[3]) {
      updatedUserData = { ...userData, subscriptionPlan: products && products[1] };
    }

    if (updatedUserData !== userData && firebaseApp) {
      const userDocRef = doc(collection(getFirestore(firebaseApp), 'Users'), userId);
      await updateDoc(userDocRef, updatedUserData);
    }
    return updatedUserData;
  }, [firebaseApp, products]);

  const plan = useMemo(
    () => resolvePlanFromUser(firebaseDocumentData, products, subscription),
    [firebaseDocumentData, products, subscription]
  );

  useEffect(() => {
    if (!firebaseApp) return;

    setisError(false);
    const authHandler = async (user: User | null) => {
      if (!user) {
        navigate('/login/');
        return;
      }

      try {
        const userDocRef = doc(collection(getFirestore(firebaseApp), 'Users'), user.uid);
        const firebaseDocument = await getDoc(userDocRef);
        let userData = firebaseDocument.data();

        const updatedUserData = await updateOldPrices(userData, user.uid);
        const subscriptionResponse = await fetchSubscriptionData(updatedUserData?.transactionReceipt);
        const hasError = subscriptionResponse === null;

        dispatch({
          type: 'loaded',
          user,
          doc: updatedUserData!,
          subscription: subscriptionResponse!,
          loadingText: 'Checking your credentials...',
        });
        if (hasError) {
          setisError(hasError);
          return;
        }
      } catch (error) {
        console.error('Error in authHandler:', error);
      }
      
    };

    return auth?.onAuthStateChanged(authHandler);
  }, [firebaseApp, dispatch, navigate, products, auth, updateOldPrices]);

  if (isLoading || !currentUser) {
    return <div className="account__container"><p>{loadingText}</p></div>;
  }

  const { cancelled: subscriptionCancelled, discount: subscriptionDiscount, endDate: subscriptionEndDate, paymentMethod: subscriptionPaymentMethod, status: subscriptionStatus } = subscription ?? {};
  const membershipSource = firebaseDocumentData?.subscriptionPlan === 'Free access' ? 'Free Access' : getMembershipSource(firebaseDocumentData?.transactionReceipt);
  const isSubscriptionActive = subscriptionStatus === 'active';
  const isTrialPeriodActive = subscriptionStatus === 'trialing';
  const isPlanActiveOrTrialing = isSubscriptionActive || isTrialPeriodActive;
  const isMemberStripe = membershipSource === 'Stripe';
  const isMemberPaypal = membershipSource === 'PayPal';
  const isDiscountedCompletely = subscriptionDiscount?.percentOff === 100;
  const isSubscriptionCancelled = subscriptionCancelled || subscriptionStatus === 'cancelled';
  const isSubscriptionExpired = subscriptionStatus === 'expired';
  const isSubscriptionSuspended = subscriptionStatus === 'suspended';
  const isSubscriptionEndDateInFuture = subscriptionEndDate !== undefined && subscriptionEndDate > Date.now();
  const isCancelButtonShowing = isMemberStripe && isPlanActiveOrTrialing && !isSubscriptionCancelled && (!isDiscountedCompletely || isPaymentMethodOnFile);
  const planAmount = (isMemberStripe || isMemberPaypal) ? plan?.unit_amount : firebaseDocumentData?.subscriptionData?.originalPrice;
  const planPrice = getPlanPrice(subscriptionDiscount?.amountOff ?? 0, subscriptionDiscount?.percentOff ?? 0, planAmount ?? 0);
  const registrationType = getRegistrationType(subscriptionDiscount?.percentOff || 0, isPaymentMethodOnFile);
  const durationType = (plan?.recurring?.interval === 'year' || plan?.recurring?.interval === 'YEAR') ? getYearsText(plan?.recurring?.interval_count) : getMonthsText(plan?.recurring?.interval_count);
  const recurringDurationText = `${plan?.recurring?.interval_count} ${durationType}`;
  const billingFrequency = getBillingCycleText(recurringDurationText);

  const detailSummaryOptions: Record<string, JSX.Element> = {
    PartialDiscountWithCardCollection: (
      <p>
        After your initial discounted membership rate of ${planPrice}, you will be subscribing to a recurring ${plan?.price} {billingFrequency} membership fee. This charge will automatically renew every {recurringDurationText}.
      </p>
    ),
    CompletelyDiscountedWithCardCollection: (
      <>
        <p>You have subscribed to a free membership for {plan?.recurring?.interval_count} {durationType}.</p>
        <p>After your initial free membership, you will be subscribing to a recurring ${convertUnitAmountToAmount(plan?.unit_amount)} {billingFrequency} membership fee. This charge will automatically renew every {recurringDurationText}.</p>
      </>
    ),
    CompletelyDiscountedWithoutCardCollection: <p>You have subscribed to a free membership for {plan?.durationMonths} {durationType}.</p>,
    StandardCardCollection: <p>You will be charged a recurring ${isMemberPaypal ? Number.parseFloat(planAmount?.toString()).toFixed(2) : convertUnitAmountToAmount(plan?.unit_amount)} {billingFrequency} membership fee. This charge will automatically renew every {recurringDurationText}.</p>,
  };

  const cancelSubscription = async (cancelImmediately?: boolean) => {
    dispatch({ type: 'loading', loadingText: 'Cancelling your membership...' });
    const requestBody = { sub: firebaseDocumentData?.transactionReceipt, cancelImmediately };
    const response = await fetch(service.API_ENDPOINTS.cancelStripeSubscription, { method: 'POST', headers: { 'Content-type': 'application/json' }, body: JSON.stringify(requestBody) });
    const subscriptionResponse: SubscriptionType = await response.json();
    dispatch({ type: 'loaded', user: currentUser!, doc: firebaseDocumentData!, subscription: subscriptionResponse, loadingText: 'Checking your credentials...' });
  };

  const renewSubscription = async () => {
    dispatch({ type: 'loading', loadingText: 'Checking your payment method...' });
    if (firebaseDocumentData?.stripeCustomer) { // Stripe user registered?
      const response = await fetch(service.API_ENDPOINTS.verifyCustomerPayment, { method: 'POST', headers: { 'Content-type': 'application/json' }, body: JSON.stringify({ id: firebaseDocumentData?.stripeCustomer }) });
      const responseJSON: any = await response.json();
      if (responseJSON.paymentExist) {
        dispatch({ type: 'loading', loadingText: 'Renewing your membership...' });
        const responseRenew = await fetch(service.API_ENDPOINTS.renewSubscription, {
          method: 'POST',
          headers: { 'Content-type': 'application/json' },
          body: JSON.stringify({ sub: firebaseDocumentData.transactionReceipt, stripeUserId: firebaseDocumentData.stripeCustomer, userId: currentUser?.uid, price: firebaseDocumentData.subscriptionPlan }),
        });
        const subscriptionResponse: SubscriptionType = await responseRenew.json();
        if (firebaseApp) {
          const userDocRef = doc(collection(getFirestore(firebaseApp), 'Users'), currentUser?.uid);
          const firebaseDocument = await getDoc(userDocRef);
          const userData = firebaseDocument.data();
          dispatch({ type: 'loaded', user: currentUser!, doc: userData!, subscription: subscriptionResponse, loadingText: 'Checking your credentials' });
        } else {
          dispatch({ type: 'loaded', user: currentUser!, doc: firebaseDocumentData!, subscription: subscriptionResponse, loadingText: 'Checking your credentials' });
        }
      } else {
        dispatch({ type: 'payment' }); // Invalid payment method
      }
    } else {
      dispatch({ type: 'payment' }); // User not registered with stripe
    }
  };

  const confirmModal = (action: string) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        const date = subscription?.endedAt ? new Date(Number(subscription?.endedAt)).toLocaleDateString(typeof window === 'undefined' ? 'en-AU' : window.navigator.language, { day: 'numeric', month: 'long', year: 'numeric' }) : new Date(Number(subscription?.endDate)).toLocaleDateString(typeof window === 'undefined' ? 'en-AU' : window.navigator.language, { day: 'numeric', month: 'long', year: 'numeric' });
        const formatDate = date === 'Invalid Date' ? new Date().toLocaleDateString(typeof window === 'undefined' ? 'en-AU' : window.navigator.language, { day: 'numeric', month: 'long', year: 'numeric' }) : date;
        return (
          <div className="confirmation-modal">
            <h1>Are you sure?</h1>
            <p>Your membership will {action.toLocaleLowerCase() === 'renew' ? 'automatically renew' : 'expire'} on {formatDate}</p>
            <button type="button" onClick={onClose}>No</button>
            <button type="button" onClick={() => { if (action === 'Cancel Immediately') { cancelSubscription(true); onClose(); return; } if (action === 'Cancel') { cancelSubscription(); onClose(); return; } renewSubscription(); onClose(); }}>Yes, {action.toLocaleLowerCase()} membership</button>
          </div>
        );
      },
    });
  };

  const showConfirmation = async (action: string) => {
    confirmModal(action);
  };

  const onRenew = () => {
    if (membershipSource === 'Stripe' && isSubscriptionActive) {
      showConfirmation('Renew');
      return;
    }
    const url = ENV.isDevelopment ? 'https://www.staging17.defeatdiabetes.com.au/pricing' : 'https://www.defeatdiabetes.com.au/pricing';
    window.location.replace(url);
  };

  const showRenewalDisclaimer = !isDiscountedCompletely && !isSubscriptionCancelled && !isSubscriptionExpired;
  const AccountDetailTab = (
    <div className="account__detail">
      <div className="account__detail-item">
        <strong>Name</strong>
        <p>{firebaseDocumentData?.firstName} {firebaseDocumentData?.lastName}</p>
      </div>
      <div className="account__detail-item">
        <strong>Email</strong>
        <p>{currentUser?.email}</p>
      </div>
    </div>
  );

  const AccountMembershipTab = (
    <div className="account__detail">
      {isError && `${plan}` !== "Free access" ? (
        <Alert severity="error" variant="standard">
          There's no valid subscription.
        </Alert>
      ) : (
        <>
          <div className="account__detail-item">
            {
              (!isMemberPaypal || subscriptionStatus !== 'expired' ) && (
                <strong>Your membership</strong>
              )
            }
         
            <div className="account__detail-membership">
              <div>
                <p>
                  {plan || (membershipSource === 'Free Access' || (recurringDurationText && !isNaN(planAmount))) ? (
                    isMemberStripe ? `${recurringDurationText} - $${convertUnitAmountToAmount(plan?.unit_amount)}`
                      : isMemberPaypal ?
                        `${recurringDurationText} - $${Number.parseFloat(planAmount?.toString()).toFixed(2)}`
                        : `${plan}` === "Free access" ?
                          `${plan}`
                          : `${plan} - $${Number.parseFloat(planPrice?.toString()).toFixed(2)}`)
                    : null
                  }
                </p>
              </div>
              <div className="account__detail-membership--statusContainer">
                {((isMemberStripe && isSubscriptionActive && !isSubscriptionCancelled) || (isMemberPaypal && isSubscriptionActive && !isSubscriptionCancelled) || (!isMemberStripe && isSubscriptionActive && isSubscriptionEndDateInFuture)) && (<ActiveStatus />)}
                {((isMemberStripe && isTrialPeriodActive && !isSubscriptionCancelled) || (isMemberPaypal && isTrialPeriodActive && !isSubscriptionCancelled) || (!isMemberStripe && isTrialPeriodActive && isSubscriptionEndDateInFuture)) && (<TrialStatus />)}
                {isMemberPaypal && isSubscriptionSuspended && (
                  <>
                    <div className="account__detail-membership--status"><SuspendedStatus /></div>
                    <div className="account__detail-membership--renew" style={{ cursor: 'pointer' }} onClick={() => onRenew()}><ExpiredStatus /></div>
                  </>
                )}
                {(isMemberStripe || isMemberPaypal) && isSubscriptionCancelled && !isSubscriptionSuspended && (
                  <>
                    <div className="account__detail-membership--status">{isSubscriptionCancelled || isPlanActiveOrTrialing ? 'Cancelled' : 'Expired'}</div>
                    <div className="account__detail-membership--renew" style={{ cursor: 'pointer' }} onClick={() => onRenew()}><ExpiredStatus /></div>
                  </>
                )}
                {(isMemberStripe || isMemberPaypal) && isSubscriptionExpired && !isSubscriptionSuspended && (
                  <>
                    <div className="account__detail-membership--status">Expired</div>
                    <div className="account__detail-membership--renew" style={{ cursor: 'pointer' }} onClick={() => onRenew()}><ExpiredStatus /></div>
                  </>
                )}
                {(!isMemberStripe && !isMemberPaypal) && !isSubscriptionEndDateInFuture && `${plan}` !== "Free access" && (
                  <>
                    <div className="account__detail-membership--status">Expired</div>
                    <div className="account__detail-membership--renew" style={{ cursor: 'pointer' }} onClick={() => onRenew()}><ExpiredStatus /></div>
                  </>
                )}
              </div>
            </div>
            {(isMemberStripe || isMemberPaypal) && (
              <>
                {(isSubscriptionCancelled || isSubscriptionExpired) && (
                  <>
                    <div className="account__detail-item--small">Your subscription has been {isSubscriptionCancelled ? 'cancelled' : `${subscriptionStatus}`}, please renew for membership access!</div>
                  </>
                )}
                {(!isSubscriptionSuspended && !isSubscriptionCancelled && !isSubscriptionExpired) && (
                  <div className="account__detail-item--small">{detailSummaryOptions[registrationType]}</div>
                )}
              </>
            )}
            {showRenewalDisclaimer && (
              <div className="account__detail-item--small">
                Subscriptions automatically renew at least 24 hours prior to the end of the current period, you can cancel your subscription by pressing the “Cancel Subscription” button below.
              </div>
            )}
            <div className="account__detail-item--small">
              {' '} For more details see our {' '} <Link to="https://www.defeatdiabetes.com.au/terms-and-conditions">Terms and Conditions</Link> and <Link to="https://www.defeatdiabetes.com.au/privacy-policy/">Privacy Policy</Link>.
            </div>
          </div>
          <div className="account__detail-item">
            <strong>Membership name</strong>
            <p>{firebaseDocumentData?.firstName} {firebaseDocumentData?.lastName}</p>
          </div>
          {(isMemberStripe || isMemberPaypal) && (
            <div className="account__detail-item">
              {
                (() => {
                  const dateTimestamp = subscription?.endedAt ?? subscription?.endDate;
                  const date = dateTimestamp ? new Date(Number(dateTimestamp)) : null;
                  const isValidDate = date && !isNaN(date.getTime());

                  const dateText = isValidDate ?
                    date.toLocaleDateString(typeof window === 'undefined' ? 'en-AU' : window.navigator.language, { day: 'numeric', month: 'long', year: 'numeric' })
                    : 'N/A';

                  const labelText = isValidDate ? (
                    isPlanActiveOrTrialing && !isSubscriptionCancelled && isTrialPeriodActive ? 'Your free trial expires on' :
                      isPlanActiveOrTrialing && !isSubscriptionCancelled && !isTrialPeriodActive ? 'Renews on' :
                        isPlanActiveOrTrialing && isSubscriptionCancelled ? 'Expires on' :
                          isSubscriptionSuspended ? 'Subscription Suspended on' :
                            isSubscriptionExpired ? 'Expired on' :
                              !isPlanActiveOrTrialing && isSubscriptionCancelled ? 'Expired on' : '') : 'Subscription Expiry';

                  return (
                    <>
                      <strong>{labelText}</strong>
                      <p>{dateText}</p>
                    </>
                  );
                })()
              }
            </div>
          )}
          {isPaymentMethodOnFile && (
            <div className="account__detail-item">
              <strong>Payment type </strong>
              <p>{`•••• •••• •••• ${subscriptionPaymentMethod?.last4}`}</p>
            </div>
          )}
          <div className="account__detail-item--small">Your membership is managed via {membershipSource}</div>
          {isCancelButtonShowing && (
            <button type="button" onClick={() => showConfirmation('Cancel')} disabled={isLoading}>Cancel Membership</button>
          )}
          {isCancelButtonShowing && (
            <button type="button" className='account__detail-item--cancel-membership-immediately' onClick={() => showConfirmation('Cancel Immediately')} disabled={isLoading}>Cancel Membership Immediately</button>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="account__container">
      <Typography variant="h2" className="account__title">Hi {firebaseDocumentData?.firstName},</Typography>
      <div className="account__tabs">
        <button type="button" onClick={() => dispatch({ type: 'set_tab', tab: 'detail' })} className={cx('account__tab', { '--active': currentTab === 'detail' })}>My details</button>
        <button type="button" onClick={() => dispatch({ type: 'set_tab', tab: 'membership' })} className={cx('account__tab', { '--active': currentTab === 'membership' })}>My membership</button>
      </div>
      {currentTab === 'detail' && AccountDetailTab}
      {currentTab === 'membership' && AccountMembershipTab}
    </div>
  );
}
